import { Dialog, Toolbar } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ItemPicker,
	CommonItemPickerOptions,
	ItemPickerOptions,
	SharePointItemPickerOptions,
	WebDavItemPickerOptions,
	ItemPickerLocation,
	ItemPickerMode
} from "../ItemPicker/ItemPicker";
import { isNil, isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { DocumentStoreType } from "client/Api/Documents/common";
import { RootFolderId as WEBDAV_ROOT_FOLDER_ID } from "client/Features/Documents/WebDav/useFolderNavigationStack";
import { useDataInputDialog } from "client/Hooks";

type SelectDocumentsFolderDialogOptions = {
	allowRootFolder: boolean;
	allowSharePoint: boolean;
	allowWebDav: boolean;
};

export type SelectDocumentsFolderDialogProps = {
	onClose: () => void;
	options: SelectDocumentsFolderDialogOptions;
	title: string;
	onSelectionDone: (selection: ItemPickerLocation) => void;
	initialLocation?: ItemPickerLocation;
};

export const SelectDocumentsFolderDialog = (props: SelectDocumentsFolderDialogProps): ReactElement => {
	const {
		options,
		onClose,
		title,
		onSelectionDone,
		initialLocation
	} = props;

	const [location, setLocation] = useState<ItemPickerLocation | undefined>(initialLocation);

	const {
		allowSharePoint,
		allowWebDav,
		allowRootFolder
	} = options;

	const isButtonDisabled: boolean = useMemo(
		(): boolean => {
			if (isNil(location)){
				return true;
			}

			if (allowRootFolder) {
				return false;
			}

			switch (location.kind) {
				case DocumentStoreType.SharePoint:
					return location.folder.isRoot;
				case DocumentStoreType.WebDav:
					return location.folder.id === WEBDAV_ROOT_FOLDER_ID;
				default:
					never(location);
			}
		},
		[allowRootFolder, location]
	);

	const itemPickerOptions: ItemPickerOptions = useMemo(
		() => {
			const { sharePoint, webDav } = documentsConfiguration();

			const sharePointOptions: SharePointItemPickerOptions = allowSharePoint
				&& sharePoint.isConfigured
					? {
						isEnabled: true,
						shouldShowPersonalViews: false,
						availableDestination: { kind: "all" }
					}
					: { isEnabled: false };

			const webDavOptions: WebDavItemPickerOptions = allowWebDav
				&& webDav && webDav.isConfigured
					? {
						isEnabled: true,
						availableDestination: { kind: "all" }
					}
					: { isEnabled: false };

			const commonOptions: CommonItemPickerOptions = {
				mode: ItemPickerMode.SingleFolder,
				creationOptions: {
					folder: true
				}
			};

			return {
				sharePointOptions,
				webDavOptions,
				commonOptions
			};
		},
		[allowSharePoint, allowWebDav]
	);

	const selectionDoneHandler = useCallback(
		(): void => {
			if (isNotNil(location)) {
				onSelectionDone(location);
				onClose();
			}
		},
		[
			onSelectionDone,
			location,
			onClose
		]
	);

	return (
		<Dialog
			trackingId="select-document-folder-dialog"
			variant="left-slider"
			onClose={onClose}
		>
			<Dialog.Header
				title={title}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<ItemPicker
					options={itemPickerOptions}
					location={location}
					onLocationChange={setLocation}
				/>
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						onClick={selectionDoneHandler}
						text={localize("_SelectFolder")}
						isDisabled={isButtonDisabled}
						trackingId="select-folder"
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Cancel")}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};

SelectDocumentsFolderDialog.displayName="SelectDocumentsFolderDialog";

export type SelectDocumentsFolderDialogInitialData = Omit<SelectDocumentsFolderDialogProps, "onClose" | "onSelectionDone">;

export function useSelectDocumentsFolderDialog(
): [
	element: ReactElement | null,
	open: (props: SelectDocumentsFolderDialogInitialData) => Promise<TryResult<ItemPickerLocation>>
] {
	return useDataInputDialog<SelectDocumentsFolderDialogInitialData, ItemPickerLocation>(
		(props, onClose, onSaved) => (
			<SelectDocumentsFolderDialog
				{...props}
				onClose={onClose}
				onSelectionDone={onSaved}
			/>
		)
	);
}